
import * as React from 'react';
import { useState, useEffect } from 'react';
import Layout from './components/Layout';
import Home from './pages/Home';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import Admin from './pages/Admin';
import Contact from './pages/Contact';
import WeddingConsulting from './pages/WeddingConsulting';
import Packages from './pages/Packages';
import Portfolio from './pages/Portfolio';
import CustomProducts from './pages/CustomProducts';
import Privacy from './pages/Privacy';
import Cookie from './pages/Cookie';
import CookieConsent from './components/CookieConsent';
import { useStore, isAdminEmail } from './services/store';
import { WHATSAPP_LINK, fetchCustomProductImageLinks } from './constants';

const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const store = useStore();

  useEffect(() => {
    // Pre-carica le immagini dei prodotti salvate nel Cloud Supabase
    fetchCustomProductImageLinks().catch(() => {});

    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash) setCurrentPage(hash);
    };
    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigate = (page: string) => {
    window.location.hash = page;
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handleLogout = () => {
    store.logout();
    navigate('home');
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <Home navigate={navigate} />;
      case 'portfolio':
        return <Portfolio navigate={navigate} />;
      case 'login':
        return <Auth mode="login" navigate={navigate} onLogin={(email, pass) => store.login(email, pass)} />;
      case 'register':
        return <Auth mode="register" navigate={navigate} onLogin={(email, pass, phone) => store.signUp(email, pass, phone || '')} />;
      case 'dashboard':
        return store.user ? (
          <Dashboard 
            user={store.user} 
            orders={store.orders}
            addOrder={store.addOrder}
            updateStatus={store.updateOrderStatus}
            navigate={navigate}
            onLogout={handleLogout}
          />
        ) : <Auth mode="login" navigate={navigate} onLogin={(email, pass) => store.login(email, pass)} />;
      case 'admin':
        return (store.user?.role === 'admin' || isAdminEmail(store.user?.email)) ? (
          <Admin 
            orders={store.orders} 
            updateStatus={store.updateOrderStatus} 
            deleteOrder={store.deleteOrder}
            onLogout={handleLogout}
            refreshOrders={store.fetchOrders}
            isLoading={store.loading}
          />
        ) : <Auth mode="login" navigate={navigate} onLogin={(email, pass) => store.login(email, pass)} />;
      case 'packages':
        return <Packages navigate={navigate} user={store.user} />;
      case 'custom-products':
      case 'prodotti-personalizzati':
        return <CustomProducts navigate={navigate} user={store.user} addOrder={store.addOrder} />;
      case 'contact':
        return <Contact />;
      case 'wedding-consulting':
        return <WeddingConsulting />;
      case 'privacy':
        return <Privacy />;
      case 'cookie':
        return <Cookie />;
      default:
        return <Home navigate={navigate} />;
    }
  };

  return (
    <div className="relative">
      <Layout 
        user={store.user} 
        onLogout={handleLogout} 
        navigate={navigate}
      >
        {renderPage()}
      </Layout>
      
      {/* Cookie Consent Banner & Preferences Modal */}
      <CookieConsent onNavigate={navigate} />

      {/* Floating WhatsApp Button */}
      <a 
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 md:w-16 md:h-16 bg-[#25D366] text-white rounded-full flex items-center justify-center shadow-2xl z-[11000] hover:scale-110 active:scale-95 transition-all animate-bounce"
        aria-label="Contattaci su WhatsApp"
      >
        <svg className="w-8 h-8 md:w-10 md:h-10 fill-current" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
};

export default App;